import React, {useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, FlexColumn, FlexRow, Label, Modal, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {NovaStudioColor} from '../shared/novaStudioColors';

export interface NovaAgentVersionEntry {
	versionNumber: number;
	createdAt: string | null;
	createdBy: string | null;
	isCurrent: boolean;
	// Whether both required prompts are written. The service refuses to restore a version that is not
	// (T3-344) — createAgent's version 1 always is one — so such a row offers no Restore.
	isRunnable: boolean;
}

export interface NovaVersionHistoryModalProps {
	versions: NovaAgentVersionEntry[];
	// Performs the actual restore (the mutation + reloading the restored content into the
	// working draft) — owned by the page that fetched `versions` in the first place (see
	// NovaStudioPage.tsx/NovaStudioShell.tsx), not by this modal. This modal only orchestrates
	// which row is busy and surfaces the outcome; it never calls Relay directly.
	onRestore: (versionNumber: number) => Promise<void>;
	onClose: () => void;
	// The list's title, matching the header button that opens it. "Version history" when omitted.
	titleId?: string;
}

const VersionRow = styled.div`
	display: grid;
	grid-template-columns: 96px 1fr 1fr 120px;
	align-items: center;
	padding: 8px 0;
	border-bottom: 1px solid ${NovaStudioColor.border};
`;

// A version's own failure to restore already surfaces through Util.CommitMutation's default
// error modal (see NovaStudioPage.tsx's onRestoreVersion) — this only needs to reset the busy
// row so the author isn't left with a stuck, unresponsive button after a failed attempt.
export function NovaVersionHistoryModal({
	versions,
	onRestore,
	onClose,
	titleId = 'nova.versionHistory.title',
}: NovaVersionHistoryModalProps): JSX.Element {
	const {formatMessage, formatDate} = useIntl();
	const [restoringVersion, setRestoringVersion] = useState<number | null>(null);

	const sortedVersions = [...versions].sort((first, second) => second.versionNumber - first.versionNumber);
	const isRestoring = restoringVersion !== null;

	const handleRestore = async (versionNumber: number): Promise<void> => {
		if (isRestoring) {
			return;
		}
		setRestoringVersion(versionNumber);
		try {
			await onRestore(versionNumber);
			onClose();
		} catch {
			setRestoringVersion(null);
		}
	};

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={onClose}>
			<Modal.Title>{formatMessage({id: titleId})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'m'}>
					<Text>
						{formatMessage({
							id: 'nova.versionHistory.description',
						})}
					</Text>
					<FlexColumn gap={'xs'}>
						<VersionRow>
							<Label>{formatMessage({id: 'nova.versionHistory.columnVersion'})}</Label>
							<Label>{formatMessage({id: 'nova.versionHistory.columnCreatedAt'})}</Label>
							<Label>{formatMessage({id: 'nova.versionHistory.columnAuthor'})}</Label>
							<Label> </Label>
						</VersionRow>
						{sortedVersions.map(version => {
							const isCurrent = version.isCurrent;
							return (
								<VersionRow key={version.versionNumber} data-cy={`nova-version-${version.versionNumber}`}>
									<Text>{version.versionNumber}</Text>
									<Text>
										{version.createdAt
											? formatDate(version.createdAt, {
													year: 'numeric',
													month: 'short',
													day: 'numeric',
													hour: '2-digit',
													minute: '2-digit',
											  })
											: '—'}
									</Text>
									<Text>{version.createdBy ?? '—'}</Text>
									{isCurrent ? (
										<Pill variant={PillVariant.SUCCESS}>
											{formatMessage({id: 'nova.versionHistory.currentLabel'})}
										</Pill>
									) : !version.isRunnable ? (
										<FlexRow justifyContent={'flex-end'}>
											<Text
												color="auxiliary"
												data-cy={`nova-restore-unavailable-${version.versionNumber}`}
											>
												{formatMessage({id: 'nova.versionHistory.notRunnable'})}
											</Text>
										</FlexRow>
									) : (
										<FlexRow justifyContent={'flex-end'}>
											<Button
												emphasis="low"
												data-cy={`nova-restore-${version.versionNumber}`}
												aria-disabled={isRestoring}
												onPress={() => void handleRestore(version.versionNumber)}
											>
												{formatMessage({
													id: 'nova.versionHistory.restoreButton',
												})}
											</Button>
										</FlexRow>
									)}
								</VersionRow>
							);
						})}
					</FlexColumn>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={onClose} dataCy="nova-version-history-close">
					{formatMessage({id: 'nova.versionHistory.closeButton'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
